import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getWorkers, deleteWorker, updateWorker } from '../../redux/features/workerSlice';
import { getJobs, fetchArchiveJobs } from '../../redux/features/jobSlice';
import { formatMonthKey, parseMonthKey } from '../../utils/workerStatsUtils';
import {
  DEFAULT_RATING_PARAMS,
  loadRatingParams,
  saveRatingParams
} from '../../utils/workerRatingUtils';
import { getBusinessType } from '../../utils/businessTypeUtils';
import WorkerForm from '../forms/WorkerForm';
import WorkerStatsCard from './WorkerStatsCard';
import { UserPlus, Settings, Info, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { canAddWorker, getMaxWorkers } from '../../utils/packageLimits';

const WorkerList = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [showRatingSettings, setShowRatingSettings] = useState(false);
  const [showRatingInfo, setShowRatingInfo] = useState(false);
  const [editingMaxWorkerId, setEditingMaxWorkerId] = useState(null);
  const [maxDailyDraft, setMaxDailyDraft] = useState('10');
  const [savingMaxWorkerId, setSavingMaxWorkerId] = useState(null);
  const [archiveYear, setArchiveYear] = useState(new Date().getFullYear());
  const [workerMonthKeys, setWorkerMonthKeys] = useState({});
  const [ratingParams, setRatingParams] = useState(loadRatingParams);

  const dispatch = useDispatch();
  const { workers, loading, error } = useSelector((state) => state.worker);
  const { archiveJobs } = useSelector((state) => state.job || { archiveJobs: [] });
  const { user } = useSelector((state) => state.auth || {});

  const getWorkerMonthKey = (workerId) =>
    workerMonthKeys[workerId] ||
    formatMonthKey(new Date().getFullYear(), new Date().getMonth());

  const handleWorkerMonthChange = (workerId, monthKey) => {
    setWorkerMonthKeys((prev) => ({ ...prev, [workerId]: monthKey }));
    const { year } = parseMonthKey(monthKey);
    if (year !== archiveYear) setArchiveYear(year);
  };

  useEffect(() => {
    const businessType = user?.result?.businessType || getBusinessType() || 'Home Appliance Technician';
    dispatch(getWorkers());
    dispatch(getJobs(businessType));
  }, [dispatch, user]);

  useEffect(() => {
    const businessType = user?.result?.businessType || getBusinessType() || 'Home Appliance Technician';
    const start = new Date(archiveYear, 0, 1, 0, 0, 0, 0);
    const end = new Date(archiveYear, 11, 31, 23, 59, 59, 999);
    dispatch(
      fetchArchiveJobs({
        businessType,
        startDate: start.toISOString(),
        endDate: end.toISOString()
      })
    );
  }, [dispatch, user, archiveYear]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const currentWorkerCount = workers?.length || 0;
  const canAdd = canAddWorker(user, currentWorkerCount);
  const maxWorkers = getMaxWorkers(user);

  const handleAddWorker = () => {
    if (!canAdd) {
      toast.error(
        `Dostigli ste limit radnika za vaš paket. Maksimalno: ${maxWorkers === Infinity ? 'neograničeno' : maxWorkers}.`
      );
      return;
    }
    setIsEdit(false);
    setSelectedWorker(null);
    setIsModalOpen(true);
  };

  const handleEditWorker = (worker) => {
    setIsEdit(true);
    setSelectedWorker(worker);
    setIsModalOpen(true);
  };

  const handleDeleteWorker = async (workerId) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovog radnika?')) {
      try {
        await dispatch(deleteWorker({ id: workerId }));
        toast.success('Radnik je uspešno obrisan');
      } catch (err) {
        toast.error('Greška pri brisanju radnika');
      }
    }
  };

  const startEditMaxDaily = (worker) => {
    setEditingMaxWorkerId(worker._id);
    setMaxDailyDraft(String(worker.maxDailyInterventions ?? 10));
  };

  const cancelEditMaxDaily = () => {
    setEditingMaxWorkerId(null);
    setMaxDailyDraft('10');
  };

  const saveMaxDaily = async (worker) => {
    const parsed = Math.min(50, Math.max(1, Number(maxDailyDraft) || 10));
    setSavingMaxWorkerId(worker._id);
    try {
      await dispatch(updateWorker({ id: worker._id, updatedWorkerData: { maxDailyInterventions: parsed } }));
      toast.success('Limit intervencija je sačuvan');
      cancelEditMaxDaily();
    } catch (err) {
      toast.error('Greška pri čuvanju limita');
    } finally {
      setSavingMaxWorkerId(null);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedWorker(null);
    setIsEdit(false);
  };

  const handleSaveRatingParams = (params) => {
    setRatingParams(params);
    saveRatingParams(params);
    toast.success('Parametri rejtinga su sačuvani');
    setShowRatingSettings(false);
  };

  const renderHeaderActions = () => (
    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
      <button
        type="button"
        onClick={() => setShowRatingInfo(true)}
        className="flex items-center justify-center gap-2 bg-purple-600 text-white px-3 py-2 text-sm rounded-lg hover:bg-purple-700 transition-colors"
      >
        <Info size={18} />
        <span className="hidden sm:inline">Rejting</span>
      </button>
      <button
        type="button"
        onClick={() => setShowRatingSettings(true)}
        className="flex items-center justify-center gap-2 bg-gray-700 text-white px-3 py-2 text-sm rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
      >
        <Settings size={18} />
        <span className="hidden sm:inline">Podešavanja</span>
      </button>
      <button
        type="button"
        onClick={handleAddWorker}
        disabled={!canAdd}
        className={`flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
          canAdd ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
        }`}
      >
        <UserPlus size={18} />
        <span className="hidden sm:inline">Dodaj radnika</span>
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-gray-900 min-h-screen p-2 sm:p-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
        </div>
      </div>
    );
  }

  if (!workers || workers.length === 0) {
    return (
      <div className="bg-gray-900 min-h-screen p-2 sm:p-4">
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 shadow-lg flex justify-between items-center gap-3">
          <h2 className="text-xl font-bold text-white">{t('workers.title', 'Radnici')}</h2>
          {renderHeaderActions()}
        </div>
        <div className="bg-gray-800 rounded-lg p-6 text-center text-gray-400 shadow-lg">
          Nema radnika. Dodajte prvog radnika.
        </div>
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
            <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <WorkerForm isEdit={isEdit} worker={selectedWorker} onClose={handleCloseModal} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-900 p-2 sm:p-4 min-h-screen">
      <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">{t('workers.title', 'Radnici')}</h2>
            <p className="text-xs text-gray-400 mt-1">
              Kartice mesečnog učinka · rejting 1–100
            </p>
          </div>
          {renderHeaderActions()}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 mb-4">
        {workers.map((worker) => (
          <WorkerStatsCard
            key={worker._id}
            worker={worker}
            archiveJobs={archiveJobs}
            monthKey={getWorkerMonthKey(worker._id)}
            onMonthChange={(key) => handleWorkerMonthChange(worker._id, key)}
            ratingParams={ratingParams}
            editingMaxWorkerId={editingMaxWorkerId}
            maxDailyDraft={maxDailyDraft}
            savingMaxWorkerId={savingMaxWorkerId}
            onStartEditMaxDaily={startEditMaxDaily}
            onCancelEditMaxDaily={cancelEditMaxDaily}
            onSaveMaxDaily={saveMaxDaily}
            onMaxDailyDraftChange={setMaxDailyDraft}
            onEdit={handleEditWorker}
            onDelete={handleDeleteWorker}
          />
        ))}
      </div>

      <div className="bg-gray-800 rounded-lg p-4 sm:p-5 shadow-lg border border-gray-700/50">
        <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
          <Info className="h-4 w-4 text-purple-400" />
          Kako se računa rejting (1–100)?
        </h3>
        <div className="text-xs text-gray-400 space-y-1.5">
          <p>
            Na kartici <span className="text-gray-300">Mesečni učinak</span> rejting je broj od 1 do 100,
            bez valute — pogodan za rad u različitim zemljama. Za izabrani mesec sabiraju se poeni iz
            četiri oblasti rada. Reklamacije i ponovni dolasci blago koriguju skor da bi se video
            kvalitet prvog izlaska — to nije kazna, već deo slike o mesecu.
          </p>
          <ul className="list-disc list-inside space-y-0.5 text-gray-500">
            <li>Ispunjenost kapaciteta (završeni / mesečni maksimum) — do {ratingParams.completedWeight} poena</li>
            <li>Udeo poslova rešenih iz prvog dolaska — do {ratingParams.firstVisitWeight} poena</li>
            <li>Efikasnost prihoda (šta ostane nakon troškova) — do {ratingParams.profitEfficiencyWeight} poena</li>
            <li>Pokrivenost adresa i aktivnih dana — do {ratingParams.addressCoverageWeight} poena</li>
            <li>
              Korekcija: {ratingParams.complaintPenalty} poena manje po reklamaciji, {ratingParams.secondVisitPenalty} poena
              manje po ponovnom dolasku
            </li>
            {ratingParams.applyCoefficient && (
              <li>Na kraju se skor usklađuje sa koeficijentom specijalizacije (1–5)</li>
            )}
          </ul>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3 sm:p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <WorkerForm isEdit={isEdit} worker={selectedWorker} onClose={handleCloseModal} />
          </div>
        </div>
      )}

      {showRatingSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-5 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Podešavanja rejtinga
              </h2>
              <button type="button" onClick={() => setShowRatingSettings(false)} className="text-gray-400 hover:text-white">
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              {[
                [
                  'completedWeight',
                  'Težina: ispunjenost kapaciteta',
                  50,
                  'Koliko je radnik ispunio planirani mesečni obim (završeni poslovi u odnosu na maksimum).'
                ],
                [
                  'firstVisitWeight',
                  'Težina: prvi izlazak',
                  50,
                  'Koliki udeo poslova je rešen već pri prvom dolasku kod klijenta.'
                ],
                [
                  'profitEfficiencyWeight',
                  'Težina: efikasnost prihoda',
                  50,
                  'Koliko od prihoda ostane nakon troškova — odnos, ne valuta.'
                ],
                [
                  'addressCoverageWeight',
                  'Težina: adrese i dani',
                  30,
                  'Raznovrsnost adresa i broj dana u mesecu sa završenim poslovima.'
                ],
                [
                  'complaintPenalty',
                  'Korekcija po reklamaciji',
                  20,
                  'Blago smanjenje skora po reklamaciji. Nije kazna — pomaže da se vidi kvalitet rada.'
                ],
                [
                  'secondVisitPenalty',
                  'Korekcija po ponovnom dolasku',
                  15,
                  'Blago smanjenje kada je za posao bio potreban još jedan dolazak.'
                ]
              ].map(([key, label, max, hint]) => (
                <div key={key}>
                  <label className="block text-xs text-gray-300 mb-1">{label}</label>
                  <input
                    type="number"
                    min={0}
                    max={max}
                    value={ratingParams[key]}
                    onChange={(e) =>
                      setRatingParams({ ...ratingParams, [key]: Number(e.target.value) || 0 })
                    }
                    className="w-full h-8 px-2.5 rounded-md border border-gray-600 bg-gray-700 text-white text-xs"
                  />
                  <p className="text-[10px] text-gray-500 mt-1 leading-snug">{hint}</p>
                </div>
              ))}
              <label className="flex items-start gap-2 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={ratingParams.applyCoefficient}
                  onChange={(e) =>
                    setRatingParams({ ...ratingParams, applyCoefficient: e.target.checked })
                  }
                  className="rounded border-gray-600 mt-0.5"
                />
                <span>
                  <span className="text-xs text-gray-300 block">Primeni koeficijent specijalizacije</span>
                  <span className="text-[10px] text-gray-500 leading-snug">
                    Skor se usklađuje sa koeficijentom specijalizacije radnika (1–5), da se u obzir uzme
                    složenost posla.
                  </span>
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setRatingParams(DEFAULT_RATING_PARAMS)}
                className="px-3 py-1.5 text-xs text-gray-300 border border-gray-600 rounded-md hover:bg-gray-700"
              >
                Podrazumevano
              </button>
              <button
                type="button"
                onClick={() => handleSaveRatingParams(ratingParams)}
                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Sačuvaj
              </button>
            </div>
          </div>
        </div>
      )}

      {showRatingInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-white">Objašnjenje rejtinga</h2>
              <button type="button" onClick={() => setShowRatingInfo(false)} className="text-gray-400 hover:text-white">
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="text-xs text-gray-300 space-y-3">
              <p>
                Kartica <span className="text-white font-medium">Mesečni učinak</span> za izabrani mesec
                prikazuje podatke iz arhive: prihod, troškovi, reklamacije, broj adresa i dana sa
                poslovima, završeni poslovi i mesečni kapacitet (radni dani × maks. intervencija/dan).
              </p>
              <p>
                Rejting 1–100 sabira poene iz težina koje podesite. Reklamacije i ponovni dolasci blago
                koriguju skor da bi se istakao kvalitet prvog izlaska — to nije kazna, već deo slike o
                radu. Ako je uključeno, skor se zatim usklađuje sa koeficijentom specijalizacije. Nema
                izraza u dinarima ili drugoj valuti.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowRatingInfo(false)}
              className="mt-4 w-full py-2 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Zatvori
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerList;
